import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CmAuthService } from '@ats/cm/core';
import { CmModalSettings } from '@ats/cm/ui';
import { AtsNotificationService } from '@ats/notifications';
import { filterNullOrUndefined } from '@ats/utility';
import { CultureService } from '@cm/core/culture';
import { LocalizationDialogData, LocalizationService } from '@cm/core/localization';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, Observable, distinctUntilChanged } from 'rxjs';
import { combineLatestWith, switchMap, take } from 'rxjs/operators';
import { LocalizationDialogComponent } from '../components/localization-dialog/localization-dialog.component';

@UntilDestroy()
@Injectable({
	providedIn: 'root',
})
export class LocalizationViewService {
	private readonly inlineEnabled = new BehaviorSubject<boolean>(false);
	readonly inlineEnabled$: Observable<boolean> = this.inlineEnabled
		.asObservable()
		.pipe(distinctUntilChanged());

	constructor(
		public readonly locService: LocalizationService,
		private readonly dialog: MatDialog,
		private readonly notificationService: AtsNotificationService,
		private readonly auth: CmAuthService,
		private readonly cultureService: CultureService
	) {
		this.auth.token$.pipe(untilDestroyed(this)).subscribe(token => {
			if (!token) {
				this.inlineEnabled.next(false);
			}
		});
	}

	toggleInline(): void {
		this.inlineEnabled.next(!this.inlineEnabled.getValue());
	}

	localize<T = any>(name: string): Observable<T | undefined> {
		return this.locService.find$(name).pipe(
			filterNullOrUndefined(),
			combineLatestWith(this.cultureService.culture$, this.cultureService.supportedCultures$),
			take(1),
			switchMap(([loc, { code }, supportedCultures]) =>
				this.dialog
					.open<LocalizationDialogComponent, LocalizationDialogData, T>(
						LocalizationDialogComponent,
						{
							...CmModalSettings.Medium,
							autoFocus: '.loc-dialog-first',
							data: { loc, language: code, supportedCultures },
						}
					)
					.afterClosed()
			)
		);
	}

	popSystemError(id: string = 'system-error'): void {
		this.locService
			.findValue$('Core/Error.GUID.SystemError')
			.pipe(take(1))
			.subscribe(message => {
				this.notificationService.popError({
					message,
					id,
				});
			});
	}
}
